import { useEffect, useState } from 'react';
import { useCopilot } from '../../api/copilot';

/**
 * What the wait is called while it lasts.
 *
 * Cosmetic on purpose — nothing on the wire says which step the agent is on —
 * so the words describe the kind of work a question over this desk involves
 * rather than claiming a phase. They turn over at Ki_UI's pace (1.7 s), slow
 * enough to be read once each.
 */
const VERBS = ['Reading the screen', 'Checking the numbers', 'Working it out', 'Drafting the answer'];
const STEP_MS = 1700;
/* Past this the rotation has been seen twice over and stops saying anything,
   so it gives way to one sentence that sets the expectation instead. */
const LONG_MS = 10000;
export const LONG_TEXT = 'Still working — this one takes a moment';
/* The one word a reader who asked for stillness sees, in place of the rotation. */
const STILL_TEXT = 'Working it out';

const REDUCE = '(prefers-reduced-motion: reduce)';

/**
 * The reader's reduced-motion preference, followed live.
 *
 * The stylesheet's blanket rule already stops CSS animation, but a word that
 * changes every 1.7 s is motion made in script, which that rule cannot reach.
 */
function useReducedMotion() {
  const [still, setStill] = useState(() => {
    try {
      return !!window.matchMedia?.(REDUCE)?.matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia?.(REDUCE);
    } catch {
      mq = null;
    }
    if (!mq) return undefined;
    const onChange = () => setStill(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener?.(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener?.(onChange);
    };
  }, []);
  return still;
}

/**
 * Rotating status words with three waiting dots, replacing the bare dots the
 * thread used to show.
 *
 * Nothing here is announced. The rotating word would talk over everything
 * else every 1.7 s, and the steady sentence a screen reader should hear is the
 * thread's own status region's (`CopilotThread`) — a second live region here
 * said it again, and again each time this indicator remounted.
 *
 * "Long" is whichever comes first — ten seconds since this indicator appeared,
 * or the provider's own `waitingLong`, which survives the indicator being
 * unmounted and remounted (a streaming line hides it, the drawer opens).
 */
export default function ThinkingStatus({ className = '' }) {
  const { waitingLong = false } = useCopilot();
  const still = useReducedMotion();
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (still) return undefined;
    const t = setInterval(() => setStep((n) => (n + 1) % VERBS.length), STEP_MS);
    return () => clearInterval(t);
  }, [still]);

  useEffect(() => {
    const t = setTimeout(() => setElapsed(true), LONG_MS);
    return () => clearTimeout(t);
  }, []);

  const long = elapsed || waitingLong;
  const text = long ? LONG_TEXT : still ? STILL_TEXT : VERBS[step];

  return (
    <span className={`cp-thinking ${className}`.trim()}>
      {/* Keyed by the word so each one mounts fresh and fades in, rather than
          the text swapping under a fade that already finished. */}
      <span className="cp-verb" key={text} aria-hidden="true">{text}</span>
      <span className="dots cp-dots" aria-hidden="true"><i /><i /><i /></span>
    </span>
  );
}
